/* eslint-disable @typescript-eslint/no-explicit-any */
// M10-15600: the GST block shared by every counter form (doctor / pharmacy / hospital, add and
// edit). Exactly one GST is the default, and that is the one mirrored onto counter_hdr and shown
// on every read-only screen.
//
// How many GSTs a counter may hold is an RBAC decision: everyone gets one, and only holders of
// counter:add-gst can add further ones.
import { Plus, Trash2 } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray } from "react-hook-form";

import { Autocomplete, Button, Input } from "src/components";
import ErrorMessage from "src/container/error-message.component";
import { GSTStatus } from "src/constants/counter-master";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";
//M10-15603
import {
  ACTIVE_GST_STATUS, //M10-15969
  DefaultGSTOptions,
  GstDetailFormRow,
  VALID_GST_NO, //M10-15969
  VALID_GST_YES,
  ValidGSTOptions,
  createEmptyGstRow,
  getDefaultGstRow,
  //M10-15603
} from "src/utils/gst-details.helper";
import { cn } from "src/utils/utils";

interface GstDetailsSectionProps {
  formInstance: any;
  /** Defaults to "gst_details"; overridable for forms that nest the counter under another key. */
  name?: string;
  disabled?: boolean;
  className?: string;
}

const GstDetailsSection = ({
  formInstance,
  name = "gst_details",
  disabled = false,
  className,
}: GstDetailsSectionProps) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
  } = formInstance;

  const { fields, append, remove, replace } = useFieldArray({ control, name });

  //M10-15603: counter:add-gst gates the *second and subsequent* GSTs, not GST itself. Without it a
  // counter still gets its one mandatory GST; the permission is what unlocks adding more.
  const { hasPermission: canAddMultipleGst, loading: permissionLoading } = usePermission(
    fineGrainedPermissionsKeys.COUNTER.ADDGST
  );

  const gstAvailable = watch("gst_available");
  const isGstAvailable = gstAvailable?.id === "Yes";

  // Keep the list in step with the Yes/No dropdown: switching to No clears it (there is nothing to
  // store), and switching to Yes opens the first row straight away, since at least one GST is
  // mandatory and that first row is necessarily the default.
  useEffect(() => {
    if (!isGstAvailable) {
      if (fields.length) {
        replace([]);
        clearErrors(name);
      }
      return;
    }

    if (!fields.length) append(createEmptyGstRow(true));
  }, [isGstAvailable]);

  const rows: GstDetailFormRow[] = watch(name) || [];
  const defaultRow = getDefaultGstRow(rows);

  // The flat gst_number / gst_status stay in the form values as read-only mirrors of the default
  // row. They are not submitted — the backend derives them from the list — but plenty of existing
  // form logic still reads them: the GST proof upload binds its attachment to `gst_number`, and the
  // Chain Pharmacy / GST Exempt toggles appear only once a GST exists. Deriving them here keeps all
  // of that working untouched instead of rewriting the same condition in eight forms.
  useEffect(() => {
    setValue("gst_number", defaultRow?.gst_number || "");
    setValue("gst_status", defaultRow?.gst_status || null);
  }, [defaultRow?.gst_number, defaultRow?.gst_status?.id, isGstAvailable]);

 //M10-15969
  const validityKey = rows.map((row) => `${row?.gst_status?.id}:${row?.valid_gst?.id}:${row?.is_default?.id}`).join("|");
  useEffect(() => {
    rows.forEach((row, index) => {
      if (row?.is_default?.id === true) return;
      if (row?.gst_status?.id !== ACTIVE_GST_STATUS && row?.valid_gst?.id !== VALID_GST_NO.id) {
        setValue(`${name}.${index}.valid_gst`, VALID_GST_NO, { shouldDirty: true });
        clearErrors(`${name}.${index}.valid_gst`);
      }
    });
  }, [validityKey]);
  //M10-15969
  if (!isGstAvailable) return null;

  /**
   * Default is single-select spread across rows: marking one default un-marks the rest here rather
   * than failing validation afterwards, which is the difference between the form correcting itself
   * and the user hunting for which other row is still flagged.
   */
  const handleDefaultChange = (index: number, option: any) => {
    setValue(`${name}.${index}.is_default`, option, { shouldDirty: true, shouldValidate: true });

    if (option?.id !== true) return;

    //M10-15603
    setValue(`${name}.${index}.valid_gst`, VALID_GST_YES, { shouldDirty: true });

    (getValues(name) || []).forEach((_row: GstDetailFormRow, rowIndex: number) => {
      if (rowIndex !== index) {
        setValue(`${name}.${rowIndex}.is_default`, DefaultGSTOptions[1], { shouldDirty: true });
      }
    });
    clearErrors(name);
  };

  /**
   * Removing a row removes it, nothing more. Having no default at all is a valid state, so
   * promoting another row here would silently change which GST counter_hdr carries.
   */
  const handleRemove = (index: number) => {
    remove(index);
  };

  //M10-15603
  const isRowDefault = (index: number) => watch(`${name}.${index}.is_default`)?.id === true;
  const isRowActive = (index: number) => watch(`${name}.${index}.gst_status`)?.id === ACTIVE_GST_STATUS; //M10-15969

  const rowErrors = (errors?.[name] as any) || {};
  // Yup's array-level tests ("mark one default", "at least one GST") land on the array itself, not
  // on any row — react-hook-form surfaces that as `.message` or `.root.message` by version.
  const sectionError = rowErrors?.message || rowErrors?.root?.message;

  //M10-15603: the one mandatory GST is opened automatically, so a user without counter:add-gst
  // never needs this button — hiding it caps them at a single GST. It stays hidden while the
  // permission is still resolving, so an action they may not have never flashes up.
  const canAddAnotherGst = canAddMultipleGst || fields.length === 0;
  const showAddGstButton = !permissionLoading && canAddAnotherGst;

  const handleAddGst = () => {
    // Guards the handler as well as the button: without the permission the only row that may be
    // created is the first one.
    if (!canAddAnotherGst) return;
    append(createEmptyGstRow(!rows?.length));
  };

  return (
    <div className={cn("w-full col-span-1 sm:col-span-2", className)}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[#A4A3A5] text-xs ml-3">GST Details*</p>
        {showAddGstButton && (
          <Button
            type="button"
            disabled={disabled}
            onClick={handleAddGst}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-[#7F56D9] border border-[#7F56D9] rounded-md disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Add GST
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        {fields.map((field: any, index: number) => (
          <div key={field.id} className="flex items-start w-full gap-2 p-3 border rounded-lg bg-gray-50/60">
            <div className="grid flex-1 grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col w-full">
                <p className="text-[#A4A3A5] text-xs ml-3 mb-1">GST*</p>
                <Input
                  disabled={disabled}
                  placeholder="GST Number"
                  className="w-full"
                  value={watch(`${name}.${index}.gst_number`) || ""}
                  onChange={(event: any) => {
                    // Uppercased on the way in: the GST format is defined in capitals and the
                    // duplicate checks compare case-insensitively, so normalising here keeps what
                    // the user sees and what gets compared identical.
                    setValue(`${name}.${index}.gst_number`, event.target.value.trim().toUpperCase(), {
                      shouldDirty: true,
                    });
                  }}
                  onBlur={() => formInstance.trigger(`${name}.${index}.gst_number`)}
                />
                {/* type is left blank on purpose: ErrorMessage only renders the real message on
                    its default branch — the named types substitute their own generic wording. */}
                <ErrorMessage
                  label="GST"
                  type=""
                  show={!!rowErrors?.[index]?.gst_number?.message}
                  message={rowErrors?.[index]?.gst_number?.message || ""}
                />
              </div>

              <div className="flex flex-col w-full">
                <p className="text-[#A4A3A5] text-xs ml-3 mb-1">GST status*</p>
                <Autocomplete
                  suggestionList={GSTStatus}
                  displayKey="name"
                  uniqueKey="id"
                  placeholder="Select status"
                  inputClassName="w-full h-[30px]"
                  disabled={disabled}
                  defaultValue={watch(`${name}.${index}.gst_status`)?.name || ""}
                  onSelectionChange={(option: any) => {
                    setValue(`${name}.${index}.gst_status`, option, { shouldDirty: true, shouldValidate: true });
                    //M10-15969
                    if (option?.id !== ACTIVE_GST_STATUS) {
                      setValue(`${name}.${index}.valid_gst`, VALID_GST_NO, { shouldDirty: true, shouldValidate: true });
                    }
                    //M10-15969
                    // The default GST has to be Active, so re-check the section rule as soon as a
                    // status changes rather than waiting for submit.
                    formInstance.trigger(name);
                  }}
                />
                <ErrorMessage
                  label="GST status"
                  type=""
                  show={!!rowErrors?.[index]?.gst_status?.message}
                  message={rowErrors?.[index]?.gst_status?.message || ""}
                />
              </div>

              <div className="flex flex-col w-full">
                <p className="text-[#A4A3A5] text-xs ml-3 mb-1">Default GST*</p>
                <Autocomplete
                  suggestionList={DefaultGSTOptions}
                  displayKey="name"
                  uniqueKey="name"
                  placeholder="Select"
                  inputClassName="w-full h-[30px]"
                  disabled={disabled}
                  defaultValue={watch(`${name}.${index}.is_default`)?.name || ""}
                  onSelectionChange={(option: any) => handleDefaultChange(index, option)}
                />
              </div>

              {/* M10-15603 */}
              <div className="flex flex-col w-full">
                <p className="text-[#A4A3A5] text-xs ml-3 mb-1">Valid GST*</p>
                <Autocomplete
                  suggestionList={isRowActive(index) ? ValidGSTOptions : [VALID_GST_NO]}
                  displayKey="name"
                  uniqueKey="name"
                  placeholder="Select"
                  inputClassName="w-full h-[30px]"
                  disabled={disabled || isRowDefault(index) || !isRowActive(index)}
                  readonly={isRowDefault(index) || !isRowActive(index)}
                  defaultValue={
                    isRowDefault(index)
                      ? VALID_GST_YES.name
                      : isRowActive(index)
                        ? watch(`${name}.${index}.valid_gst`)?.name || ""
                        : VALID_GST_NO.name
                  }
                  onSelectionChange={(option: any) => {
                    if (isRowDefault(index) || !isRowActive(index)) return;
                    setValue(`${name}.${index}.valid_gst`, option, { shouldDirty: true, shouldValidate: true });
                  }}
                />
                <ErrorMessage
                  label="Valid GST"
                  type=""
                  show={!!rowErrors?.[index]?.valid_gst?.message}
                  message={rowErrors?.[index]?.valid_gst?.message || ""}
                />
              </div>
              {/* //M10-15603 */}
            </div>

            <button
              type="button"
              aria-label="Remove GST"
              // The last row cannot go: Available GST = "Yes" means at least one GST. Clearing the
              // list entirely is done by switching Available GST to "No".
              disabled={disabled || fields.length === 1}
              onClick={() => handleRemove(index)}
              className="mt-6 text-[#A4A3A5] hover:text-red-500 disabled:opacity-30 disabled:hover:text-[#A4A3A5]"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <ErrorMessage label="GST Details" type="" show={!!sectionError} message={sectionError || ""} />
    </div>
  );
};

export default GstDetailsSection;
